<template>
  <div class="base64-upload">
    <el-upload
      ref="uploadRef"
      v-bind="$attrs"
      :class="{ 'hide-upload': modelValue }"
      :action="action"
      :headers="headers"
      :data="data"
      :show-file-list="false"
      :on-success="handleSuccess"
      :on-error="handleError"
      :on-remove="handleRemove"
      :before-upload="beforeUpload"
      :disabled="disabled"
    >
      <template #trigger>
        <slot name="trigger">
          <el-icon class="avatar-uploader-icon"><Plus /></el-icon>
          <!-- <el-button :disabled="disabled" type="primary">选择文件</el-button> -->
        </slot>
      </template>

      <template #tip v-if="!modelValue && $slots.tip">
        <slot name="tip"></slot>
      </template>
    </el-upload>

    <!-- 文件预览区域 -->
    <div v-if="modelValue" class="preview-container">
      <div class="preview-content">
        <!-- 图片预览 -->
        <img
          v-if="isImage"
          :src="previewUrl"
          class="preview-image"
          alt=""
          id="previewQrCode"
          :style="{ 'max-width': imageWidth, 'max-height': imageHeight }"
        />
        <!-- 非图片文件预览 -->
        <div v-else class="file-preview">
          <el-icon :size="40">
            <Document />
          </el-icon>
          <div class="file-name">{{ fileName }}</div>
          <div class="file-size">{{ fileSize }}</div>
        </div>

        <!-- 操作按钮 -->
        <div class="preview-actions" v-if="!disabled">
          <el-icon @click="handleReplace" :disabled="disabled">
            <Upload />
          </el-icon>
          <el-icon @click="handleClear" :disabled="disabled">
            <Delete />
          </el-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from "vue";
import { Document } from "@element-plus/icons-vue";
import { getToken, getImageUrl, getTenant } from "@/utils/auth";
import { ElMessage } from "element-plus";
const props = defineProps({
  // v-model 绑定值 (Base64字符串)
  modelValue: {
    type: String,
    default: ""
  },
  // 是否禁用
  disabled: {
    type: Boolean,
    default: false
  },
  // 接受的文件类型
  accept: {
    type: String,
    default: ""
  },
  // 图片最大宽度
  imageWidth: {
    type: String,
    default: "80px"
  },
  // 图片最大高度
  imageHeight: {
    type: String,
    default: "80px"
  },
  // 文件大小限制 (MB)，0 表示不限制
  maxSize: {
    type: Number,
    default: 0
  },
  // 图片宽度上限 (px)，0 表示不限制
  limitWidth: {
    type: Number,
    default: 0
  },
  // 图片高度上限 (px)，0 表示不限制
  limitHeight: {
    type: Number,
    default: 0
  },
  // 上传地址 (保留el-upload的原生功能)
  action: {
    type: String,
    default: ""
  },
  // 上传附加参数 (el-upload data，随 FormData 一起提交)
  data: {
    type: Object,
    default: () => ({})
  },
  fullImgUrl: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["update:modelValue", "change", "error"]);

const uploadRef = ref(null);
const fileName = ref("");
const fileSize = ref("");
const isImage = ref(true);

const headers = computed(() => ({
  Authorization: getToken(), // 从store获取token
  Tenant: getTenant(),
  "X-Requested-With": "XMLHttpRequest"
}));

const previewUrl = computed(() => {
  if (!props.modelValue) return "";
  return isImage.value && props.fullImgUrl
    ? getImageUrl(props.modelValue)
    : props.modelValue
});

// 摒弃二维码预览功能
const getQrCodeImg = async orginUrl => {
  const response = await fetch(
    `/gw-api/customer/keyAccountManager/viewQrCode/${orginUrl}`,
    {
      headers: {
        Tenant: getTenant(),
        "Content-Type": "application/json",
        Authorization: getToken()
      }
    }
  );
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  const data = await response.blob();
   const url = URL.createObjectURL(data);
    const previewQrCodeNode = document.getElementById('previewQrCode')
    previewQrCodeNode.src = url
};

// 监听modelValue变化处理回填
watch(
  () => props.modelValue,
  newVal => {
    if (!newVal) {
      fileName.value = "";
      fileSize.value = "";
      isImage.value = true;
    }
  }
);

// 校验被拦截时的标记：el-upload 在 before-upload 返回 false 时会触发 on-remove，
// 此时不应清空已上传的文件
let rejectedByValidation = false;

// 上传前校验：文件大小、图片宽高（均未配置则直接放行）
const beforeUpload = file => {
  return new Promise(resolve => {
    if (props.maxSize > 0 && file.size > props.maxSize * 1024 * 1024) {
      const limitText =
        props.maxSize >= 1
          ? `${props.maxSize}MB`
          : `${Math.round(props.maxSize * 1024)}KB`;
      ElMessage.error(`文件大小不能超过${limitText}`);
      rejectedByValidation = true;
      return resolve(false);
    }
    if (
      (props.limitWidth > 0 || props.limitHeight > 0) &&
      file.type.startsWith("image/")
    ) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (props.limitWidth > 0 && img.width > props.limitWidth) {
          ElMessage.error(
            `图片宽度不能超过${props.limitWidth}px，当前${img.width}px`
          );
          rejectedByValidation = true;
          return resolve(false);
        }
        if (props.limitHeight > 0 && img.height > props.limitHeight) {
          ElMessage.error(
            `图片高度不能超过${props.limitHeight}px，当前${img.height}px`
          );
          rejectedByValidation = true;
          return resolve(false);
        }
        resolve(true);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        // 无法解析尺寸的图片跳过宽高校验
        resolve(true);
      };
      img.src = url;
    } else {
      resolve(true);
    }
  });
};

// 清除文件
const handleClear = () => {
  emit("update:modelValue", "");
  emit("change", null);
};

// 替换文件
const handleReplace = () => {
  handleClear();
  uploadRef.value.$el.querySelector("input").click();
};

// 移除文件
const handleRemove = () => {
  if (rejectedByValidation) {
    rejectedByValidation = false;
    return;
  }
  handleClear();
};

const handleSuccess = (response, file) => {
  if (response.code === 200) {
    //emit("update:modelValue", "http://192.168.1.42:9090/static/" + response.data);
    if(!props.fullImgUrl){
     emit("update:modelValue",getImageUrl(response.data));
    }else{
     emit("update:modelValue", response.data);

    }
    ElMessage.success("文件导入成功");
  } else {
    ElMessage.error("文件导入失败");
  }
};
const handleError = err => {
  console.error("上传失败:", err);
};
</script>

<style scoped>
.base64-upload {
  width: 100%;
}

.hide-upload :deep(.el-upload) {
  display: none;
}

.preview-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: inline-block;
  width: 80px;
  height: 80px;
}
.preview-container:hover .preview-actions {
  display: flex;
}

.preview-content {
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: v-bind(imageWidth);
  max-height: v-bind(imageHeight);
  display: block;
  border-radius: 4px;
}

.file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
}

.file-name {
  margin-top: 5px;
  font-size: 14px;
  word-break: break-all;
  text-align: center;
}

.file-size {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.preview-actions {
  display: none;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
}
.el-icon {
  color: #fff;
  font-size: 20px;
  cursor: pointer;
}
.el-icon:first-child {
  margin-right: 8px;
}
.el-icon.avatar-uploader-icon {
  border: 1px dashed #ddd;
  font-size: 28px;
  color: #8c939d;
  width: 80px;
  height: 80px;
  text-align: center;
}
</style>